import type { AreasOverview } from '@ft/shared';
import { useI18n } from '../i18n';
import { getAreaId, switchArea } from '../lib/area';
import { eur, pct } from '../lib/format';
import { useLoad } from './ui';
import { Chip, Panel } from '../lib/collapse';

/**
 * Gesamtübersicht für den Delivery Head: Kennzahlen aller Bereiche nebeneinander und die Summe.
 * Ein Klick auf einen Bereich wechselt dorthin (Cockpit darunter zeigt dann diesen Bereich).
 */
export function AreasOverviewPanel({ year }: { year: number }) {
  const { t } = useI18n();
  const { data } = useLoad<AreasOverview>(`/api/overview/areas?year=${year}`);
  if (!data) return null;
  const sum = (k: 'targetCents' | 'orderedCents' | 'billedCents' | 'openCents' | 'projectsAtRisk' | 'urgentIssues' | 'checkIssues') => data.areas.reduce((x, a) => x + a[k], 0);
  const total = { target: sum('targetCents'), ordered: sum('orderedCents'), billed: sum('billedCents'), open: sum('openCents') };
  const current = getAreaId();

  return (
    <section className="page areasov" aria-labelledby="areasov-h">
      <div className="pagehead"><h2 className="sechead" id="areasov-h">{t('dh.title', { y: year })}</h2><span className="spacer" /><span className="muted small">{t('dh.hint')}</span></div>
      <div className="kpis">
        <div className="kpi"><div className="lbl">{t('ck.target', { y: year })}</div><div className="val num">{eur(total.target)}</div><div className="sub">{t('dh.areas', { n: data.areas.length })}</div></div>
        <div className="kpi"><div className="lbl">{t('ck.ordered')}</div><div className="val num">{eur(total.ordered)}</div><div className="sub">{total.target ? t('ck.orderedSub', { p: pct(total.ordered / total.target) }) : '–'}</div></div>
        <div className="kpi">
          <div className="lbl">{t('ck.billed')}</div><div className="val num">{eur(total.billed)}</div>
          <div className="sub">{total.target ? t('ck.billedSub', { p: pct(total.billed / total.target), pace: pct(data.pace) }) : '–'}</div>
        </div>
        <div className="kpi"><div className="lbl">{t('ck.open')}</div><div className="val num">{eur(total.open)}</div><div className="sub">{t('dh.issues', { u: sum('urgentIssues'), c: sum('checkIssues') })}</div></div>
      </div>
      <Panel id="start.areas" title={t('dh.tableTitle', { n: data.areas.length })}
        summary={<>
          {data.areas.reduce((x, a) => x + a.projectsAtRisk, 0) > 0 && <Chip tone="crit">{t('dh.col.risk')} <b>{data.areas.reduce((x, a) => x + a.projectsAtRisk, 0)}</b></Chip>}
          {sum('urgentIssues') > 0 && <Chip tone="neg">{t('dh.urgent', { n: sum('urgentIssues') })}</Chip>}
        </>}>
      <div className="tablewrap">
        <table>
          <thead><tr>
            <th>{t('app.area')}</th><th className="r">{t('pz.col.target')}</th><th className="r">{t('pz.col.ordered')}</th><th className="r">{t('pz.col.billed')}</th>
            <th>{t('pz.col.reach')}</th><th className="r">{t('pz.col.open')}</th><th className="r">{t('dh.col.risk')}</th><th>{t('nav.issues')}</th><th />
          </tr></thead>
          <tbody>
            {data.areas.map((a) => {
              const reach = a.targetCents ? a.billedCents / a.targetCents : null;
              const behind = reach !== null && reach < data.pace * 0.8;
              return (
                <tr key={a.id} className={`click${a.id === current ? ' current' : ''}`} tabIndex={0} onClick={() => switchArea(a.id, '/')} onKeyDown={(e) => { if (e.key === 'Enter') switchArea(a.id, '/'); }}>
                  <td><b>{a.name}</b>{!a.member && <span className="tag">{t('area.viewOnly')}</span>}{a.id === current && <span className="tag">{t('dh.current')}</span>}</td>
                  <td className="r num">{a.targetCents ? eur(a.targetCents) : <span className="muted">–</span>}</td>
                  <td className="r num">{eur(a.orderedCents)}</td>
                  <td className="r num">{eur(a.billedCents)}</td>
                  <td>{reach === null ? <span className="muted">{t('pz.health.noTarget')}</span> : (
                    <div className="dhreach">
                      <div className="meter" aria-label={t('dh.reachLabel', { p: pct(reach), pace: pct(data.pace) })}><i style={{ width: `${Math.min(100, reach * 100)}%`, background: behind ? 'var(--crit)' : 'var(--accent)' }} /><span className="pc" style={{ left: `${Math.min(100, data.pace * 100)}%` }} /></div>
                      <span className="num small">{pct(reach)}</span>
                    </div>
                  )}</td>
                  <td className="r num">{eur(a.openCents)}</td>
                  <td className="r num">{a.projectsAtRisk ? <span className="pill crit">{a.projectsAtRisk}</span> : <span className="muted">0</span>}</td>
                  <td>{a.urgentIssues > 0 && <span className="pill neg">{t('dh.urgent', { n: a.urgentIssues })}</span>} {a.checkIssues > 0 && <span className="pill crit">{t('dh.check', { n: a.checkIssues })}</span>}{!a.urgentIssues && !a.checkIssues && <span className="muted">–</span>}</td>
                  <td className="r"><span className="btn ghost" style={{ padding: 0 }}>{t('dh.open')}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </Panel>
    </section>
  );
}
